import { Button } from '@acpaas-ui/react-components';
import moment from 'moment';
import { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { fetchReset } from '../actions/fetch.actions';
import AttachmentsList from '../components/attachments/AttachmentsList';
import Datepicker from '../components/forms/Datepicker';
import TextArea from '../components/forms/TextArea';
import TextField from '../components/forms/TextField';
import EmailStakeholdersAction from '../components/stakeholders/EmailStakeholdersAction';
import { STAKEHOLDER_DOSSIER_PATHS } from '../constants/dossier.constants';
import { MODALS } from '../constants/modals.constants';
import { ATTACHMENT_TYPES } from '../constants/settings.constants';
import { TEMPLATE_CATEGORIES_SUFFIXES } from '../constants/templates.constants';
import StaticTable from '../containers/core/StaticTable';
import DossierTemplates from '../containers/dossier-templates/DossierTemplates';
import formValidators from '../form-validators';
import { useAttachmentCRUD } from '../hooks/useAttachmentCRUD.hook';
import { useModal } from '../hooks/useModal.hook';
import { useResourceInput } from '../hooks/useResourceInput';
import { getAttachmentsForContactMoment } from '../lib/dossier.utils';
import { getTemplateCategory } from '../lib/template.utils';
import { CONTACTMOMENT_STAKEHOLDERS_RESOURCE } from '../resources/contactmoments.stakeholders.resource';
import { DOSSIER_CONTACTMOMENTS_RESOURCE } from '../resources/dossier.contactmoments.resource';
import contactMomentsService from '../services/api/contactmoments.service';
import AttachmentDetailsModal from './AttachmentDetailsModal';
import AttachmentUploadModal from './AttachmentUploadModal';
import DeleteWarningModal from './DeleteWarningModal';
import Modal from './Modal';
import ModalTitle from './ModalTitle';
import StakeholderAddModal from './StakeholderAddModal';
import StakeholderDetailModal from './StakeholderDetailModal';

const ContactMomentDetailModal = ({
  attachments,
  loadingAttachments,
  dossierSchools,
  form,
  dossierType,
  dossierId,
  isOpen,
  onUpdated,
  onDelete,
  onClose,
  onAttachmentsRefetch
}) => {
  const dispatch = useDispatch();
  const modalContentRef = useRef(null);
  const stakeholderAddModal = useModal(MODALS.STAKEHOLDER_ADD_MODAL);
  const stakeholderDetailModal = useModal(MODALS.STAKEHOLDER_DETAIL_MODAL);
  const deleteContactMomentModal = useModal(MODALS.CONTACTMOMENTS_DELETE_WARNING_MODAL);
  const [stakeholderDetailModalData, setStakeholderDetailModalData] = useState({});

  const attachmentCRUD = useAttachmentCRUD(dossierId);

  const resource = {
    isFetching: false,
    isSubmitting: form.isSubmitting,
    getValue: (field) => form?.data?.[field],
    setValue: (value, field, options = {}) => {
      form.submitStart();
      contactMomentsService.update(
        dossierId,
        form.data?.id,
        { [field]: value },
        {
          onSuccess: () => {
            form.submitOk();
            form.input(value, field);
            onUpdated(
              field === 'date' ? moment(value, 'DD/MM/YYYY').format('YYYY-MM-DD') : value,
              field,
              'contactMoment',
              form.data?.id
            );
            dispatch(fetchReset(DOSSIER_CONTACTMOMENTS_RESOURCE));
            options.onSuccess();
          },
          onError: (message) => {
            form.submitError({ [field]: message });
            options.onError(message);
          }
        }
      );
    },
    validators: {
      date: formValidators.date.dateTextField,
      startTime: formValidators.time.hourMinuteField,
      endTime: formValidators.time.hourMinuteField
    }
  };

  const handleUpdated = (value, field, contactmomentId) => {
    onUpdated(value, field, 'contactMoment', contactmomentId);
    form.input(value, field);
    dispatch(
      fetchReset({
        ...CONTACTMOMENT_STAKEHOLDERS_RESOURCE,
        key: `${CONTACTMOMENT_STAKEHOLDERS_RESOURCE.key}_${form.data.id}`
      })
    );
  };

  const handleStakeholderOpenClick = (data) => {
    setStakeholderDetailModalData(data);
    stakeholderDetailModal.onOpen();
  };

  const handleDeleteRequest = () => {
    deleteContactMomentModal.onOpen();
  };

  const handleDeleteConfirm = (onError, onSuccess) => {
    contactMomentsService.remove(dossierId, form.data.id, {
      onError,
      onSuccess: () => {
        onSuccess();
        onDelete();
        handleClose();
      }
    });
  };

  const handleClose = () => {
    form.load({});
    onClose();
    dispatch(fetchReset(DOSSIER_CONTACTMOMENTS_RESOURCE));
    dispatch(
      fetchReset({
        ...CONTACTMOMENT_STAKEHOLDERS_RESOURCE,
        key: `${CONTACTMOMENT_STAKEHOLDERS_RESOURCE.key}_${form.data.id}`
      })
    );
  };

  return (
    <>
      <Modal
        ref={modalContentRef}
        title={<ModalTitle title="Contactmoment" />}
        isOpen={isOpen}
        className="spo-o-contactmoment-detail-modal"
        onClose={onClose}
        isSubmitting={form.isSubmitting}
        onCancel={onClose}
        onCancelLabel="Sluiten"
        disableConfirm
        width={1000}
        extraFooterActions={
          !form.data?.readOnly &&
          onDelete && (
            <Button type="danger" outline onClick={handleDeleteRequest}>
              Verwijderen
            </Button>
          )
        }
      >
        <div className="row u-margin-bottom-sm">
          <div className="col-xs-12 col-md-4">
            <Datepicker
              required
              {...useResourceInput('date', resource, '', { ignoreTouched: true })}
              label="Datum contactmoment"
              readOnly={form.data?.readOnly}
            />
          </div>
          <div className="col-xs-12 col-md-3">
            <TextField
              label="Starttijd"
              {...useResourceInput('startTime', resource)}
              readOnly={form.data?.readOnly}
              mask="99:99"
            />
          </div>
          <div className="col-xs-12 col-md-3">
            <TextField
              label="Eindtijd"
              {...useResourceInput('endTime', resource)}
              readOnly={form.data?.readOnly}
              mask="99:99"
            />
          </div>
        </div>
        <div className="row u-margin-bottom-md">
          <div className="col-xs-12 col-md-12">
            <TextField label="Onderwerp" {...useResourceInput('subject', resource)} readOnly={form.data?.readOnly} />
          </div>
        </div>
        <div className="row">
          <div className="col-xs-12 col-md-12">
            <StaticTable
              resource={{
                ...CONTACTMOMENT_STAKEHOLDERS_RESOURCE,
                key: `${CONTACTMOMENT_STAKEHOLDERS_RESOURCE.key}_${form.data.id}`
              }}
              onRowClick={form.data?.readOnly ? undefined : handleStakeholderOpenClick}
              data={form.data?.involved}
              actions={
                form.data?.readOnly
                  ? [
                      <EmailStakeholdersAction
                        key="stakeholder-email"
                        className="u-margin-right-sm"
                        stakeholders={form.data?.involved}
                      />
                    ]
                  : [
                      <EmailStakeholdersAction
                        key="stakeholder-email"
                        className="u-margin-right-sm"
                        stakeholders={form.data?.involved}
                      />,
                      <Button
                        key="stakeholder-add"
                        size="small"
                        className="u-margin-right-sm"
                        iconRight="ai-add"
                        onClick={stakeholderAddModal.onOpen}
                      >
                        Betrokkene toevoegen
                      </Button>
                    ]
              }
              responsive
            />
          </div>
        </div>
        <div className="row u-margin-bottom-sm">
          <div className="col-xs-12 col-md-12">
            <TextArea label="Nota's" {...useResourceInput('description', resource)} readOnly={form.data?.readOnly} />
          </div>
        </div>
        <div className="row ">
          <div className="col-xs-12 col-md-12">
            <AttachmentsList
              className="u-margin-bottom-lg"
              title="Bestanden"
              attachments={getAttachmentsForContactMoment(form.data, attachments)}
              loading={loadingAttachments}
              noAttachmentsText="Er zijn momenteel nog geen bestanden toegevoegd."
              onOpen={attachmentCRUD.onAttachmentOpen}
              onUpload={
                form.data?.readOnly
                  ? null
                  : () => {
                      attachmentCRUD.onAttachmentAddOpen(form.data.id, ATTACHMENT_TYPES.CONTACT_MOMENT);
                    }
              }
              templates={
                <DossierTemplates
                  category={getTemplateCategory(dossierType, TEMPLATE_CATEGORIES_SUFFIXES.CONTACTMOMENT)}
                />
              }
            />
          </div>
        </div>
      </Modal>
      {stakeholderAddModal.isOpen && (
        <StakeholderAddModal
          {...stakeholderAddModal}
          dossierSchools={dossierSchools}
          onUpdated={handleUpdated}
          dossierId={dossierId}
          dossierPath={STAKEHOLDER_DOSSIER_PATHS.CONTACTMOMENTS}
          itemId={form.data.id}
        />
      )}
      {stakeholderDetailModal.isOpen && (
        <StakeholderDetailModal
          {...stakeholderDetailModal}
          data={stakeholderDetailModalData}
          onUpdated={handleUpdated}
          dossierId={dossierId}
          dossierPath={STAKEHOLDER_DOSSIER_PATHS.CONTACTMOMENTS}
          itemId={form.data.id}
          write={true}
          read={true}
        />
      )}
      {deleteContactMomentModal.isOpen && (
        <DeleteWarningModal {...deleteContactMomentModal} onConfirm={handleDeleteConfirm} />
      )}
      {attachmentCRUD.uploadModal.isOpen && (
        <AttachmentUploadModal {...attachmentCRUD.uploadModal} onConfirm={attachmentCRUD.onAttachmentAdd} />
      )}
      {attachmentCRUD.detailsModal.isOpen && (
        <AttachmentDetailsModal
          {...attachmentCRUD.detailsModal}
          dossierId={dossierId}
          form={attachmentCRUD.attachmentDetailForm}
          onUpdate={attachmentCRUD.onAttachmentUpdate}
          onUpdateSuccess={onAttachmentsRefetch}
          onDelete={attachmentCRUD.deleteModal.onOpen}
        />
      )}
      {attachmentCRUD.deleteModal.isOpen && (
        <DeleteWarningModal
          {...attachmentCRUD.deleteModal}
          warningText="Weet je zeker dat je deze bijlage wilt verwijderen?"
          onConfirm={attachmentCRUD.onAttachmentDelete}
        />
      )}
    </>
  );
};

export default ContactMomentDetailModal;
